import { useCallback, useLayoutEffect, useState, type RefObject } from 'react'

export type AnchorSide = 'top' | 'bottom' | 'left' | 'right'
export type AnchorAlign = 'start' | 'end' | 'center'

export interface AnchoredPosition {
  top: number
  left: number
  /** The side actually used, which may differ from the request after flipping. */
  side: AnchorSide
}

/**
 * Fixed-viewport coordinates for a popover anchored to a trigger.
 *
 * WHY THIS EXISTS. `Tooltip` and `Dropdown` positioned themselves with
 * `position: absolute` inside the trigger's own box, which works only while no
 * ancestor establishes an overflow context. Two of ours do: `Card` is
 * `overflow-hidden` and `Table` is `overflow-x-auto`. So a tooltip inside a card
 * was CLIPPED, and a menu on the last row of a table turned the table into a
 * SCROLL REGION instead of overflowing it — both reported from the roles screen,
 * and both present anywhere else a menu sits in a table (the member list and the
 * tenants console).
 *
 * Viewport coordinates plus a portal is the fix, because `position: fixed`
 * escapes every overflow ancestor. The cost is that the popover no longer moves
 * with the page, which is why the callers close on scroll rather than trying to
 * track it — a menu that follows the page while its row scrolls away is worse
 * than one that closes.
 *
 * FLIPPING IS ON THE CROSS AXIS ONLY and is what the last-row case needs: a menu
 * with no room below opens upward instead. Clamping keeps it on screen when
 * neither side fits.
 */
export function useAnchoredPosition(
  triggerRef: RefObject<HTMLElement>,
  popoverRef: RefObject<HTMLElement>,
  {
    isOpen,
    side = 'bottom',
    align = 'end',
    gap = 6,
    margin = 8,
  }: {
    isOpen: boolean
    side?: AnchorSide
    align?: AnchorAlign
    /** Space between trigger and popover. */
    gap?: number
    /** Minimum distance from the viewport edge. */
    margin?: number
  },
) {
  const [position, setPosition] = useState<AnchoredPosition | null>(null)

  const measure = useCallback(() => {
    const trigger = triggerRef.current
    const popover = popoverRef.current
    if (!trigger || !popover) return

    const anchor = trigger.getBoundingClientRect()
    const { offsetWidth: width, offsetHeight: height } = popover
    const { innerWidth: vw, innerHeight: vh } = window

    let used: AnchorSide = side
    if (side === 'bottom' && anchor.bottom + gap + height > vh - margin
        && anchor.top - gap - height > margin) {
      used = 'top'
    } else if (side === 'top' && anchor.top - gap - height < margin
        && anchor.bottom + gap + height < vh - margin) {
      used = 'bottom'
    } else if (side === 'right' && anchor.right + gap + width > vw - margin
        && anchor.left - gap - width > margin) {
      used = 'left'
    } else if (side === 'left' && anchor.left - gap - width < margin
        && anchor.right + gap + width < vw - margin) {
      used = 'right'
    }

    let top: number
    let left: number
    if (used === 'bottom' || used === 'top') {
      top = used === 'bottom' ? anchor.bottom + gap : anchor.top - gap - height
      left =
        align === 'end' ? anchor.right - width
        : align === 'start' ? anchor.left
        : anchor.left + anchor.width / 2 - width / 2
    } else {
      left = used === 'right' ? anchor.right + gap : anchor.left - gap - width
      top =
        align === 'end' ? anchor.bottom - height
        : align === 'start' ? anchor.top
        : anchor.top + anchor.height / 2 - height / 2
    }

    setPosition({
      side: used,
      top: clamp(top, margin, vh - height - margin),
      left: clamp(left, margin, vw - width - margin),
    })
  }, [triggerRef, popoverRef, side, align, gap, margin])

  // LAYOUT effect, not a plain one: the popover is rendered before it is placed,
  // so measuring after paint would show it at 0,0 for a frame -- a visible jump
  // in the corner of the screen every time a menu opens.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null)
      return
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [isOpen, measure])

  return position
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), Math.max(min, max))
}
